import React, { useState, useEffect, useCallback } from 'react';
import { adminAPI } from '../services/api';
import { dateFr } from '../services/dates';

/**
 * ADMINISTRATION → DOCUMENTS (lot D.1b) — réservé au super-administrateur
 * ========================================================================
 * Règle : doc/specs_techniques/documents.md (§4 familles, §5 purge, §6 conservation)
 * Spec  : doc/modules/admin_panel.md § 6 bis · Maquette : admin_maquette.html, vue « Documents » (validée le 2026-10-07)
 *
 * Quatre blocs, de haut en bas : À PURGER (récapitulatif → purge), DURÉES par famille, INVENTAIRE,
 * DOSSIERS EN CONSERVATION. ⚠️ La purge efface l'OCTET, garde la PREUVE ; elle ne part qu'après un
 * récapitulatif chiffré, et le serveur refuse si les chiffres ont changé entre-temps (`attendu`).
 */

const DUREES = [0, 1, 2, 3, 4, 5, 10];

/** « 2,1 Go », « 90 Mo » — octets lisibles. */
export function octetsLisibles(n) {
    const v = Number(n) || 0;
    if (v >= 1024 ** 3) return `${(v / 1024 ** 3).toFixed(1).replace('.', ',')} Go`;
    if (v >= 1024 ** 2) return `${Math.round(v / 1024 ** 2)} Mo`;
    if (v >= 1024) return `${Math.round(v / 1024)} Ko`;
    return `${v} o`;
}
const nombre = (n) => (Number(n) || 0).toLocaleString('fr-FR');
const momentFr = (v) => (v ? new Date(v).toLocaleDateString('fr-FR', { timeZone: 'Europe/Paris' })
    + ' à ' + new Date(v).toLocaleTimeString('fr-FR', { timeZone: 'Europe/Paris', hour: '2-digit', minute: '2-digit' }) : '');

/** L'exemple qui rend la durée concrète (maquette). */
function exemple(f, duree) {
    if (duree === null) return <span className="tag tag-warn">jamais purgé — en attente des archives / du DPO</span>;
    const date = `01/09/${2027 + duree}`;
    if (f.cle === 'gestion') return <>objet clos en 2026-2027 (réunion, projet, version) → purgeable au <b>{date}</b></>;
    return <>dossier 2026-2027 → purgeable au <b>{date}</b></>;
}

function AdminDocuments({ onMessage, onErreur }) {
    const [donnees, setDonnees] = useState(null);
    const [durees, setDurees] = useState({});
    const [filtreDept, setFiltreDept] = useState('');
    const [recap, setRecap] = useState(false);
    const [enCours, setEnCours] = useState(false);
    const [succes, setSucces] = useState(null);

    // ⚠️ Aucun rappel de parent dans ces dépendances (leçon du lot 1.3 bis).
    const charger = useCallback(async () => {
        try {
            const r = await adminAPI.getDocuments();
            setDonnees(r.data);
            setDurees(Object.fromEntries(r.data.familles.map((f) => [f.cle, f.duree])));
        } catch (e) {
            onErreur(e.response?.data?.error || "Lecture de l'inventaire des documents impossible");
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps
    useEffect(() => { charger(); }, [charger]);

    if (!donnees) return <div className="a-view"><p className="hint"><i className="fa-solid fa-circle-notch fa-spin" /> Lecture de l'inventaire…</p></div>;

    const famille = (cle) => donnees.familles.find((f) => f.cle === cle);
    const libelleFamille = (cle) => famille(cle)?.libelle || cle;
    const ap = donnees.a_purger;
    const modifie = donnees.familles.filter((f) => f.modifie_le).sort((a, b) => String(b.modifie_le).localeCompare(String(a.modifie_le)))[0];
    const departements = [...new Set(donnees.lignes.map((l) => l.departement))].sort();
    const lignes = donnees.lignes.filter((l) => !filtreDept || l.departement === filtreDept);
    const total = (k) => lignes.reduce((a, l) => a + (Number(l[k]) || 0), 0);
    const nbConservation = donnees.conservations.reduce((a, c) => a + c.nb_documents, 0);
    // Recette du 2026-10-07 : l'exemple change dès la liste déroulante, « À purger » seulement à l'enregistrement —
    // sans le dire, on croit le réglage pris en compte. Les durées CHANGÉES, PAS ENREGISTRÉES, se signalent.
    const nonEnregistrees = donnees.familles.filter((f) => (durees[f.cle] ?? null) !== f.duree);
    const raisonPurgeGrisee = `Aucun document n'a atteint sa durée de conservation${donnees.prochaine_echeance ? ` (prochaine échéance : ${dateFr(donnees.prochaine_echeance)})` : ''}.`
        + (nonEnregistrees.length ? ' Des durées sont modifiées mais pas enregistrées.' : '');

    const enregistrerDurees = async () => {
        try {
            const r = await adminAPI.saveDureesDocuments(durees);
            onMessage(r.data.message);
            await charger();
            window.dispatchEvent(new Event('documents:echeance'));       // le bandeau se relit
        } catch (e) { onErreur(e.response?.data?.error || "Les durées n'ont pas pu être enregistrées"); }
    };

    const purger = async () => {
        setEnCours(true);
        try {
            const r = await adminAPI.purgerDocuments(ap.nb);
            setRecap(false);
            setSucces(r.data);
            await charger();
            window.dispatchEvent(new Event('documents:echeance'));
        } catch (e) {
            setRecap(false);
            onErreur(e.response?.data?.error || 'La purge a échoué');
            await charger();
        } finally { setEnCours(false); }
    };

    return (
        <div className="a-view">
            <div className="note">
                <i className="fa-solid fa-lock" />
                <div>Réservé au <b>super-administrateur</b> : la durée de conservation vaut pour <b>toute l'université</b>. La purge efface le <b>contenu</b> des fichiers et garde la <b>preuve</b> (nom, taille, empreinte, date, attestations, historique).</div>
            </div>

            {/* 1. À purger */}
            {ap.nb > 0 ? (
                <div className="a-card docs-echeance">
                    <div className="a-card-h">
                        <i className="fa-solid fa-box-archive" style={{ color: '#d97706' }} /> À purger
                        <button className="a-btn a-btn-danger a-btn-sm" style={{ marginLeft: 'auto' }} onClick={() => setRecap(true)}>
                            <i className="fa-solid fa-box-archive" /> Purger…
                        </button>
                    </div>
                    <div className="docs-gros">{nombre(ap.nb)} documents — {octetsLisibles(ap.octets)}</div>
                    <div className="hint" style={{ marginTop: 4 }}>
                        ont atteint leur durée de conservation : {ap.detail.map((x, i) => (
                            <React.Fragment key={i}>{i > 0 && ' — '}{libelleFamille(x.famille)} · {x.departement} · {x.annee || 'année inconnue'} : <b>{nombre(x.nb)}</b></React.Fragment>
                        ))}. Les dossiers en conservation sont exclus.
                    </div>
                </div>
            ) : (
                <div className="a-card docs-echeance vide">
                    <div className="a-card-h">
                        <i className="fa-solid fa-circle-check" style={{ color: '#16a34a' }} /> À purger
                        {/* Un bouton grisé DIT pourquoi (recette du 2026-10-07). */}
                        <span style={{ marginLeft: 'auto' }} title={raisonPurgeGrisee}>
                            <button className="a-btn a-btn-secondary a-btn-sm" disabled title={raisonPurgeGrisee}><i className="fa-solid fa-box-archive" /> Purger…</button>
                        </span>
                    </div>
                    <div className="docs-gros">Aucun document à purger</div>
                    <div className="hint" style={{ marginTop: 4 }}>
                        {donnees.prochaine_echeance ? <>Prochaine échéance : <b>{dateFr(donnees.prochaine_echeance)}</b>.</> : 'Aucune échéance à venir avec les durées actuelles.'}
                    </div>
                </div>
            )}

            {/* 2. Durées */}
            <div className="a-card">
                <div className="a-card-h">
                    <i className="fa-solid fa-hourglass-half" /> Durées de conservation
                    {modifie && <span className="hint" style={{ marginLeft: 'auto', fontWeight: 600 }}>Modifié par {modifie.modifie_par || '—'} le {momentFr(modifie.modifie_le)}</span>}
                </div>
                {nonEnregistrees.length > 0 && (
                    <div className="mail-band warn docs-non-enregistre" role="status">
                        <i className="fa-solid fa-triangle-exclamation" />
                        <div>
                            <b>Durées modifiées, non enregistrées</b> ({nonEnregistrees.map((f) => f.libelle).join(', ')}) — « À purger » et le bandeau
                            se recalculent à l'<b>enregistrement</b> : cliquez « Enregistrer les durées » sous le tableau.
                        </div>
                    </div>
                )}
                <table className="tbl">
                    <thead><tr><th>Famille</th><th>Durée</th><th>Exemple</th><th style={{ textAlign: 'right' }}>Documents</th></tr></thead>
                    <tbody>
                        {donnees.familles.map((f) => (
                            <tr key={f.cle}>
                                <td><div className="docs-famille">{f.libelle}</div><div className="docs-contenu">{f.contenu}</div></td>
                                <td>
                                    <select className="docs-select" aria-label={`Durée — ${f.libelle}`}
                                            value={durees[f.cle] === null ? 'jamais' : String(durees[f.cle])}
                                            onChange={(e) => setDurees({ ...durees, [f.cle]: e.target.value === 'jamais' ? null : Number(e.target.value) })}>
                                        <option value="jamais">Jamais</option>
                                        {DUREES.map((d) => <option key={d} value={d}>{d === 0 ? "Fin de l'année du dossier" : `${d} an${d > 1 ? 's' : ''} après`}</option>)}
                                    </select>
                                </td>
                                <td className="docs-exemple">{exemple(f, durees[f.cle] ?? null)}</td>
                                <td style={{ textAlign: 'right' }}>{f.utilisee ? nombre(f.nb_documents) : <span className="hint">pas encore utilisée</span>}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                <div className="mail-band info" style={{ marginTop: 12 }}>
                    <i className="fa-solid fa-circle-info" />
                    <div>La durée se compte en <b>années universitaires après celle du dossier</b>. <b>Actes</b> : pas de purge tant que les archives de l'université / le DPO n'ont pas donné la durée officielle — elle se saisit ici, sans développement. Un document purgé reste dans les <b>sauvegardes</b> de la base jusqu'à leur rotation (les 10 dernières en production).</div>
                </div>
                <button className={'a-btn a-btn-primary a-btn-sm' + (nonEnregistrees.length ? ' docs-a-enregistrer' : '')} onClick={enregistrerDurees}>
                    <i className="fa-solid fa-check" /> Enregistrer les durées{nonEnregistrees.length ? ` (${nonEnregistrees.length} modifiée${nonEnregistrees.length > 1 ? 's' : ''})` : ''}
                </button>
            </div>

            {/* 3. Inventaire */}
            <div className="a-card">
                <div className="a-card-h">
                    <i className="fa-solid fa-chart-column" /> Inventaire
                    <select className="docs-select" style={{ marginLeft: 'auto' }} value={filtreDept} onChange={(e) => setFiltreDept(e.target.value)} aria-label="Département">
                        <option value="">Tous les départements</option>
                        {departements.map((d) => <option key={d} value={d}>{d}</option>)}
                    </select>
                </div>
                <table className="tbl">
                    <thead><tr><th>Département</th><th>Famille</th><th>Année</th><th className="n">Documents</th><th className="n">Volume en base</th><th className="n">Purgés</th><th className="n">En conservation</th><th className="n">À échéance</th></tr></thead>
                    <tbody>
                        {lignes.length === 0 && <tr><td colSpan={8} className="hint" style={{ textAlign: 'center' }}>Aucun document.</td></tr>}
                        {lignes.map((l, i) => (
                            <tr key={i}>
                                <td><span className="tag">{l.departement}</span></td><td>{libelleFamille(l.famille)}</td><td>{l.annee || <span className="hint">inconnue</span>}</td>
                                <td className="n">{nombre(l.nb)}</td><td className="n">{octetsLisibles(l.octets)}</td>
                                <td className="n">{l.purges ? nombre(l.purges) : '—'}</td><td className="n">{l.en_conservation ? nombre(l.en_conservation) : '—'}</td>
                                <td className="n">{l.echus ? <b style={{ color: '#b45309' }}>{nombre(l.echus)}</b> : '—'}</td>
                            </tr>
                        ))}
                    </tbody>
                    <tfoot>
                        <tr><td colSpan={3}>Total</td><td className="n">{nombre(total('nb'))}</td><td className="n">{octetsLisibles(total('octets'))}</td>
                            <td className="n">{nombre(total('purges'))}</td><td className="n">{nombre(total('en_conservation'))}</td><td className="n">{nombre(total('echus'))}</td></tr>
                    </tfoot>
                </table>
                <p className="hint" style={{ marginTop: 8 }}>Un document purgé pèse 0 : seule sa ligne (nom, taille d'origine, empreinte) reste en base.</p>
            </div>

            {/* 4. Dossiers en conservation */}
            <div className="a-card">
                <div className="a-card-h">
                    <i className="fa-solid fa-shield-halved" /> Dossiers en conservation
                    <span className="tag tag-warn" style={{ marginLeft: 'auto' }}>{donnees.conservations.length} dossier{donnees.conservations.length > 1 ? 's' : ''} · {nombre(nbConservation)} document{nbConservation > 1 ? 's' : ''}</span>
                </div>
                <p className="hint" style={{ marginBottom: 10 }}>Leurs documents ne sont <b>jamais purgés</b>, même échus. La conservation se <b>pose et se lève dans le dossier</b> (panneau Stage ou Certification), par un administrateur du département ou le super-administrateur.</p>
                <table className="tbl">
                    <thead><tr><th>Département</th><th>Dossier</th><th>Motif</th><th>Posée par</th><th className="n">Documents</th></tr></thead>
                    <tbody>
                        {donnees.conservations.length === 0 && <tr><td colSpan={5} className="hint" style={{ textAlign: 'center' }}>Aucun dossier en conservation.</td></tr>}
                        {donnees.conservations.map((c) => (
                            <tr key={c.id}>
                                <td><span className="tag">{c.departement}</span></td><td>{c.dossier}</td><td>{c.motif}</td>
                                <td>{c.pose_par_nom}<div className="hint">le {momentFr(c.pose_le)}</div></td><td className="n">{c.nb_documents}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Le récapitulatif AVANT la purge (jamais confirm) */}
            {recap && (
                <div className="admin-modal-overlay" onClick={() => { if (!enCours) setRecap(false); }}>
                    <div className="admin-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-label="Purger les documents arrivés à échéance">
                        <div className="admin-modal-h">
                            <i className="fa-solid fa-box-archive" style={{ color: '#b91c1c' }} /> Purger les documents arrivés à échéance
                            <button className="x" onClick={() => { if (!enCours) setRecap(false); }} aria-label="Fermer">&times;</button>
                        </div>
                        <div className="admin-modal-b">
                            <p style={{ fontSize: 13, margin: '0 0 8px' }}><b>{nombre(ap.nb)} documents — {octetsLisibles(ap.octets)}</b></p>
                            <table className="tbl" style={{ marginBottom: 6 }}>
                                <thead><tr><th>Famille</th><th>Département</th><th>Année</th><th className="n">Documents</th><th className="n">Volume</th></tr></thead>
                                <tbody>{ap.detail.map((x, i) => (
                                    <tr key={i}><td>{libelleFamille(x.famille)}</td><td>{x.departement}</td><td>{x.annee || '—'}</td><td className="n">{nombre(x.nb)}</td><td className="n">{octetsLisibles(x.octets)}</td></tr>
                                ))}</tbody>
                            </table>
                            <div className="recap-purge">
                                <div className="bloc part"><h4><i className="fa-solid fa-trash-can" /> Ce qui part</h4>le <b>contenu</b> des fichiers : « Voir » répondra « Document purgé le … ».</div>
                                <div className="bloc reste"><h4><i className="fa-solid fa-shield" /> Ce qui reste</h4>nom, taille, <b>empreinte SHA-256</b>, date de dépôt, attestations du maître, déclarations, contrôles, historique.</div>
                            </div>
                            {donnees.conservations.length > 0 && (
                                <div className="mail-band info"><i className="fa-solid fa-shield-halved" /><div><b>{donnees.conservations.length} dossier{donnees.conservations.length > 1 ? 's' : ''} en conservation</b> {donnees.conservations.length > 1 ? 'sont exclus' : 'est exclu'} ({nombre(nbConservation)} document{nbConservation > 1 ? 's' : ''}).</div></div>
                            )}
                            <div className="mail-band ko"><i className="fa-solid fa-triangle-exclamation" /><div><b>Irréversible</b> dans l'application. Une copie reste dans les sauvegardes jusqu'à leur rotation (les 10 dernières).</div></div>
                        </div>
                        <div className="admin-modal-f">
                            <button className="a-btn a-btn-secondary" onClick={() => setRecap(false)} disabled={enCours}>Annuler</button>
                            <button className="a-btn a-btn-danger" onClick={purger} disabled={enCours}>
                                <i className={`fa-solid ${enCours ? 'fa-circle-notch fa-spin' : 'fa-box-archive'}`} /> Purger {nombre(ap.nb)} documents
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* La confirmation APRÈS */}
            {succes && (
                <div className="admin-modal-overlay" onClick={() => setSucces(null)}>
                    <div className="admin-modal sm" onClick={(e) => e.stopPropagation()} role="dialog" aria-label="Purge effectuée">
                        <div className="admin-modal-h"><i className="fa-solid fa-circle-check" style={{ color: '#16a34a' }} /> Purge effectuée
                            <button className="x" onClick={() => setSucces(null)} aria-label="Fermer">&times;</button></div>
                        <div className="admin-modal-b" style={{ fontSize: 13 }}>
                            <b>{nombre(succes.purges)} documents purgés ({octetsLisibles(succes.octets)} libérés).</b> Leur preuve reste : nom, taille, empreinte, attestations, historique.
                        </div>
                        <div className="admin-modal-f"><button className="a-btn a-btn-primary" onClick={() => setSucces(null)}>Fermer</button></div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default AdminDocuments;
